/**
 * Family Safety Score (PROJECT_BRIEF §22.3) — home-page hero card.
 *
 * Renders a 0-100 ring with the band ('great' | 'good' | 'attention' |
 * 'concern') and a list of the top factors that pulled points off.
 * Computed server-side from the analytics firehose so the math stays
 * deterministic and explainable.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Sparkles } from 'lucide-react';

import { analyticsApi, type SafetyScore } from '@/api/analytics';
import { cn } from '@/lib/utils';

const BAND_COPY: Record<SafetyScore['band'], { label: string; ring: string; fill: string }> = {
  great: {
    label: 'Great',
    ring: 'text-emerald-500',
    fill: 'bg-emerald-50 text-emerald-700',
  },
  good: {
    label: 'Good',
    ring: 'text-sky-500',
    fill: 'bg-sky-50 text-sky-700',
  },
  attention: {
    label: 'Worth a look',
    ring: 'text-amber-500',
    fill: 'bg-amber-50 text-amber-700',
  },
  concern: {
    label: 'Action needed',
    ring: 'text-rose-500',
    fill: 'bg-rose-50 text-rose-700',
  },
};

function ScoreRing({ score, band }: { score: number; band: SafetyScore['band'] }) {
  const r = 32;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - score / 100);
  return (
    <div className="relative size-20">
      <svg viewBox="0 0 80 80" className="size-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-[color:var(--kz-color-surface-sunken)]" />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
          className={cn('transition-[stroke-dashoffset] duration-500', BAND_COPY[band].ring)}
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="text-xl font-semibold tabular-nums">{score}</span>
      </div>
    </div>
  );
}

export function SafetyScoreCard() {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['safety-score', 7],
    queryFn: () => analyticsApi.safetyScore(7),
    refetchInterval: 60_000,
    retry: false,
  });

  if (q.isPending) {
    return (
      <div className="rounded-lg border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-5">
        <p className="text-sm text-[color:var(--kz-color-ink-muted)]">Loading family score…</p>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-lg border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-5">
        <p className="text-sm text-[color:var(--kz-color-ink-muted)]">
          Safety score will appear once your family has some activity.
        </p>
      </div>
    );
  }

  const { score, band, factors } = q.data;
  const reasons = factors.filter((f) => f.penalty > 0);
  const positives = factors.filter((f) => f.penalty === 0 && f.weight > 0);

  return (
    <div className="rounded-lg border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-5">
      <div className="flex items-start gap-4">
        <ScoreRing score={score} band={band} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="size-3.5 text-[color:var(--kz-color-ink-muted)]" />
            <p className="text-xs uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
              Family Safety Score · last 7 days
            </p>
          </div>
          <p className="mt-0.5 text-lg font-semibold leading-tight">
            {BAND_COPY[band].label}
            <span className={cn('ml-2 inline-flex items-center rounded-full px-2 py-0.5 align-middle text-xs font-medium', BAND_COPY[band].fill)}>
              {score}/100
            </span>
          </p>
          <p className="mt-1 text-xs text-[color:var(--kz-color-ink-secondary)]">
            {reasons.length === 0
              ? 'All factors look healthy.'
              : `${reasons.length} factor${reasons.length === 1 ? '' : 's'} pulling points off.`}
          </p>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--kz-color-brand-primary)] hover:underline"
          >
            {open ? 'Hide breakdown' : 'How is this calculated?'}
            <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-2 border-t border-[color:var(--kz-color-border-subtle)] pt-3">
          {[...reasons, ...positives].map((f) => (
            <div key={f.slug} className="flex items-center justify-between gap-3 text-xs">
              <div className="min-w-0">
                <p className="truncate font-medium text-[color:var(--kz-color-ink-primary)]">
                  {f.name}
                </p>
                <p className="truncate text-[color:var(--kz-color-ink-muted)]">{f.value}</p>
              </div>
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 tabular-nums',
                  f.penalty > 0
                    ? 'bg-rose-50 text-rose-700'
                    : 'bg-emerald-50 text-emerald-700',
                )}
              >
                {f.penalty > 0 ? `−${f.penalty}` : '✓'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
